import type { RecordingTiming } from "./multitrack-model";

export type TempoEvent = { tick: number; bpm: number };

export type MasterClockPlan = {
  ppq: number;
  startTick: number;
  plannedStartAudioTime: number;
  tempoMap: TempoEvent[];
};

function normalizedTempoMap(events: TempoEvent[]): TempoEvent[] {
  const valid = events
    .filter((event) => Number.isFinite(event.tick) && Number.isFinite(event.bpm) && event.bpm > 0)
    .map((event) => ({ tick: Math.max(0, Math.round(event.tick)), bpm: event.bpm }))
    .sort((left, right) => left.tick - right.tick);
  if (!valid.length || valid[0].tick !== 0) valid.unshift({ tick: 0, bpm: 120 });
  return valid.filter((event, index) => index === valid.length - 1 || event.tick !== valid[index + 1].tick);
}

function secondsBetweenTicks(fromTick: number, toTick: number, ppq: number, tempoMap: TempoEvent[]): number {
  if (toTick === fromTick) return 0;
  if (toTick < fromTick) return -secondsBetweenTicks(toTick, fromTick, ppq, tempoMap);
  const events = normalizedTempoMap(tempoMap);
  let seconds = 0;
  let cursor = fromTick;
  while (cursor < toTick) {
    const current = [...events].reverse().find((event) => event.tick <= cursor) ?? events[0];
    const next = events.find((event) => event.tick > cursor);
    const segmentEnd = Math.min(toTick, next?.tick ?? toTick);
    seconds += (segmentEnd - cursor) * 60 / (current.bpm * ppq);
    cursor = segmentEnd;
  }
  return seconds;
}

export function createMasterClockPlan(input: {
  ppq: number;
  bpm: number;
  startTick?: number;
  plannedStartAudioTime: number;
  tempoMap?: TempoEvent[];
}): MasterClockPlan {
  return {
    ppq: Math.max(1, Math.round(input.ppq)),
    startTick: Math.max(0, Math.round(input.startTick ?? 0)),
    plannedStartAudioTime: input.plannedStartAudioTime,
    tempoMap: normalizedTempoMap(input.tempoMap ?? [{ tick: 0, bpm: input.bpm }]),
  };
}

export function audioTimeAtTick(plan: MasterClockPlan, tick: number): number {
  return plan.plannedStartAudioTime + secondsBetweenTicks(plan.startTick, tick, plan.ppq, plan.tempoMap);
}

export function tickAtAudioTime(plan: MasterClockPlan, audioTime: number): number {
  if (audioTime <= plan.plannedStartAudioTime) {
    const initialBpm = normalizedTempoMap(plan.tempoMap)[0].bpm;
    return plan.startTick + (audioTime - plan.plannedStartAudioTime) * initialBpm * plan.ppq / 60;
  }
  let low = plan.startTick;
  let high = low + plan.ppq * 16;
  while (audioTimeAtTick(plan, high) < audioTime) high *= 2;
  for (let iteration = 0; iteration < 48; iteration += 1) {
    const middle = (low + high) / 2;
    if (audioTimeAtTick(plan, middle) < audioTime) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

export function createRecordingTiming(plannedStartAudioTime: number, manualCalibrationMs = 0): RecordingTiming {
  return {
    plannedStartAudioTime,
    observedRecordingStartAudioTime: null,
    recordingOffsetMs: 0,
    manualCalibrationMs,
  };
}

export function observeRecordingStart(timing: RecordingTiming, observedRecordingStartAudioTime: number): RecordingTiming {
  return {
    ...timing,
    observedRecordingStartAudioTime,
    recordingOffsetMs: timing.plannedStartAudioTime === null
      ? 0
      : (observedRecordingStartAudioTime - timing.plannedStartAudioTime) * 1000,
  };
}

export function effectiveRecordingOffsetMs(timing: RecordingTiming): number {
  return timing.recordingOffsetMs + timing.manualCalibrationMs;
}
